import * as React from 'react'

import { makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'

import { DeliveryStatus } from '~/client/graph'
import * as Icons from '~/client/src/shared/components/Icons'
import Delivery from '~/client/src/shared/models/Delivery'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'

import { areObjectsEqual } from '../../../utils/util'
import DeliveryStatusLabel from '../../DeliveryStatusLabel/DeliveryStatusLabel'
import CompactHeaderBar from '../CompactHeaderBar'

interface IProps {
  delivery: Delivery
  status: DeliveryStatus
  isConcreteDirectOrder: boolean
  onCloseClick: () => void
  companiesStore: CompaniesStore
  showMenuPopup: () => void
  setMenuAnchor?: (ref: HTMLElement) => void
}

// SitemapAttributeTag styles itself from unlayered SCSS, so these need `!` to
// win. They used to live in CompactHeaderBar.scss, reaching into delivery DOM
// from the shared header; they belong to the delivery consumer instead.
const TAG_CLASS = [
  'tw:h-5!',
  'tw:[&_.hierarchy-delivery-attribute-tag]:flex',
  'tw:[&_.hierarchy-delivery-attribute-tag]:h-5!',
  'tw:[&_.hierarchy-delivery-attribute-tag-content]:flex',
  'tw:[&_.hierarchy-delivery-attribute-tag-content]:items-center',
  'tw:[&_.hierarchy-delivery-attribute-tag-content>span]:truncate',
  'tw:[&_.hierarchy-delivery-attribute-tag-content>span]:text-app-micro!',
].join(' ')

const notSpecified = 'Not specified'
const newDelivery = 'New delivery'
const deleted = 'Deleted'

@observer
export default class DeliveryCompactHeaderBar extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)

    this.localProps = props
    makeObservable(this)
  }

  public componentWillReceiveProps(nextProps: Readonly<IProps>): void {
    runInAction(() => {
      if (
        !areObjectsEqual(
          this.localProps.delivery || {},
          nextProps.delivery || {},
        )
      ) {
        this.localProps.delivery = nextProps.delivery
      }
      if (
        this.localProps.isConcreteDirectOrder !==
        this.props.isConcreteDirectOrder
      ) {
        this.localProps.isConcreteDirectOrder = this.props.isConcreteDirectOrder
      }
      if (this.localProps.showMenuPopup !== nextProps.showMenuPopup) {
        this.localProps.showMenuPopup = nextProps.showMenuPopup
      }
    })
  }

  public render() {
    const { delivery, onCloseClick } = this.props

    return (
      <CompactHeaderBar
        titleCaption={this.deliveryTitleCaption}
        startDate={delivery?.startDate}
        endDate={delivery?.endDate}
        titleIconEl={this.titleIcon}
        isCreationMode={!delivery}
        renderStatus={this.deliveryStatusRenderer}
        onCloseClick={onCloseClick}
      />
    )
  }

  private deliveryStatusRenderer = (className: string): JSX.Element => {
    const { delivery, showMenuPopup, setMenuAnchor } = this.localProps

    if (delivery.isDeleted) {
      return (
        <div
          className={`${className} tw:ml-1.5 tw:rounded-sm tw:border tw:border-solid tw:border-destructive tw:px-1.5 tw:text-destructive`}
        >
          {deleted}
        </div>
      )
    }

    return (
      <DeliveryStatusLabel
        className={`${className} ${TAG_CLASS} tw:ml-1.5`}
        status={delivery.status}
        cancellationReason={delivery.cancellationReason}
        showMenuPopup={showMenuPopup}
        setMenuAnchor={setMenuAnchor}
      />
    )
  }

  private get deliveryTitleCaption(): string {
    const { delivery, companiesStore } = this.props

    return delivery
      ? `${delivery.codeToDisplay(companiesStore) || notSpecified}`
      : newDelivery
  }

  private get titleIcon(): JSX.Element {
    return this.props.isConcreteDirectOrder ? (
      <Icons.ConcreteDirect />
    ) : (
      <Icons.DeliveryHeaderBar />
    )
  }
}
